import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Upload, FileText, Trash2, X, Check, Loader, MessageSquare,
  ChevronDown, ChevronRight, FolderOpen, Bot, FileCheck, Play, Plus,
  Copy, RefreshCw, Maximize2, Clock, Zap, BarChart2, List, Edit3,
  Scissors, LogOut, Eye, Download, Folder, FolderPlus
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import './WorkspacePage.css';

const BASE_AGENTS = [
  {
    id: 'writer',
    name: 'Escritor',
    icon: '✍️',
    color: '#f59e0b',
    description: 'Redacta contenido tecnico',
    longDescription: 'Especializado en generar contenido escrito: memorias, informes, descripciones tecnicas. Sintetiza informacion de multiples documentos fuente en texto coherente y bien estructurado.',
    isBuiltin: true
  },
  {
    id: 'document_analyst',
    name: 'Analista',
    icon: '📄',
    color: '#3b82f6',
    description: 'Analiza documentos',
    longDescription: 'Especializado en extraer y analizar informacion de documentos. Identifica datos clave segun las instrucciones proporcionadas y genera analisis estructurados.',
    isBuiltin: true
  }
];

const AGENT_STYLES = [
  { value: 'formal', label: 'Formal' },
  { value: 'technical', label: 'Tecnico' },
  { value: 'conversational', label: 'Conversacional' }
];

const AGENT_EXTENSIONS = [
  { value: 'concise', label: 'Conciso' },
  { value: 'balanced', label: 'Equilibrado' },
  { value: 'detailed', label: 'Detallado' }
];

const formatCitations = (content) => {
  if (!content) return [{ type: 'text', content: '' }];
  const regex = /\[Fuente (\d+): ([^\]]+)\]/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      const textBetween = content.slice(lastIndex, match.index);
      if (textBetween.trim()) {
        parts.push({ type: 'text', content: textBetween });
      }
    }
    parts.push({ type: 'citation', number: match[1], document: match[2] });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < content.length) {
    const remaining = content.slice(lastIndex);
    if (remaining.trim()) {
      parts.push({ type: 'text', content: remaining });
    }
  }
  return parts.length > 0 ? parts : [{ type: 'text', content }];
};

function WorkspacePage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const outputRef = useRef(null);
  const editTextareaRef = useRef(null);

  // State - Proyectos y Documentos
  const [projects, setProjects] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [templateDoc, setTemplateDoc] = useState(null);
  const [instructionsDoc, setInstructionsDoc] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentFolder, setCurrentFolder] = useState(null);
  const [folderPath, setFolderPath] = useState([]);

  // State - Sesiones
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [editingSessionTitle, setEditingSessionTitle] = useState('');

  // State - UI
  const [expandedSections, setExpandedSections] = useState({
    projects: true,
    documents: true,
    sessions: false,
    agents: true
  });
  const [activeTab, setActiveTab] = useState('documents');
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);

  // State - Workflow (canvas de agentes)
  const [workflowNodes, setWorkflowNodes] = useState([]);
  const [workflowConnections, setWorkflowConnections] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [connectingFrom, setConnectingFrom] = useState(null);
  const [draggingNode, setDraggingNode] = useState(null);
  const canvasRef = useRef(null);

  // State - Ejecucion
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionProgress, setExecutionProgress] = useState(0);
  const [executionStatus, setExecutionStatus] = useState('');
  const [completedTasks, setCompletedTasks] = useState([]);
  const [taskQueue, setTaskQueue] = useState([]);
  const [activeAgents, setActiveAgents] = useState([]);

  // State - Ejecuciones de workflow (cada una con sus versiones)
  const [workflowExecutions, setWorkflowExecutions] = useState([]);
  const [activeExecutionId, setActiveExecutionId] = useState(null);

  // Obtener ejecución activa y sus datos
  const activeExecution = workflowExecutions.find(e => e.id === activeExecutionId);
  const versions = activeExecution?.versions || [];
  const currentVersionIndex = activeExecution?.currentVersionIndex ?? -1;
  const currentOutput = activeExecution?.versions[activeExecution?.currentVersionIndex]?.content || '';

  // State - Agentes personalizados
  const [customAgents, setCustomAgents] = useState([]);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [agentForm, setAgentForm] = useState({
    name: '',
    base_agent_type: 'writer',
    style: 'formal',
    extension: 'balanced',
    system_prompt: ''
  });

  // State - RAG Chat
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatFilterDocs, setChatFilterDocs] = useState([]);
  const chatEndRef = useRef(null);

  // State - Selection Menu
  const [selectionMenu, setSelectionMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    text: '',
    selectionStart: 0,
    selectionEnd: 0,
    showInput: false,
    inputMode: null,
    inputValue: '',
    result: null,
    showResult: false
  });
  const [selectionLoading, setSelectionLoading] = useState(false);

  const saveVersionToApi = async (conversationId, content, label, agentType = null) => {
    if (!conversationId || !content) return null;
    try {
      const response = await api.post(`/conversations/${conversationId}/versions`, {
        content,
        label,
        agent_type: agentType
      });
      return response.data;
    } catch (err) {
      console.error('Error saving version:', err);
      return null;
    }
  };

  const addVersionToExecution = async (executionId, content, label = 'Version', agentType = null) => {
    const newVersion = {
      id: Date.now(),
      content,
      label,
      timestamp: new Date().toLocaleTimeString()
    };
    setWorkflowExecutions(prev => prev.map(exec => {
      if (exec.id === executionId) {
        const newVersions = [...exec.versions, newVersion];
        return { ...exec, versions: newVersions, currentVersionIndex: newVersions.length - 1 };
      }
      return exec;
    }));

    if (!executionId.startsWith('local-')) {
      await saveVersionToApi(executionId, content, label, agentType);
    }
  };

  const goToVersion = (index) => {
    if (!activeExecutionId) return;
    setWorkflowExecutions(prev => prev.map(exec => {
      if (exec.id === activeExecutionId && index >= 0 && index < exec.versions.length) {
        return { ...exec, currentVersionIndex: index };
      }
      return exec;
    }));
  };

  const createWorkflowExecution = async (nodes, connections) => {
    const sortedLabels = nodes.map(n => n.agentName);
    const title = sortedLabels.join(' → ');

    const workflowState = {
      nodes: JSON.parse(JSON.stringify(nodes)),
      connections: JSON.parse(JSON.stringify(connections)),
      icons: nodes.map(n => n.agentIcon),
      colors: nodes.map(n => n.agentColor)
    };

    let conversationId = null;
    try {
      const response = await api.post('/conversations/', {
        project_id: currentProject.id,
        title: title,
        workflow_state: workflowState
      });
      conversationId = response.data.id;
      setCurrentSession(conversationId);
    } catch (err) {
      console.error('Error creating session:', err);
      conversationId = `local-${Date.now()}`;
    }

    const newExecution = {
      id: conversationId,
      label: title,
      icons: workflowState.icons,
      colors: workflowState.colors,
      nodes: workflowState.nodes,
      connections: workflowState.connections,
      versions: [],
      currentVersionIndex: -1,
      createdAt: new Date().toISOString()
    };
    setWorkflowExecutions(prev => [...prev, newExecution]);
    setActiveExecutionId(conversationId);
    return conversationId;
  };

  // Workflow functions
  const addWorkflowNode = (agentId, x, y, agentData = null) => {
    let agent = agentData;
    if (!agent) {
      agent = BASE_AGENTS.find(a => a.id === agentId);
      if (!agent) {
        const customAgent = customAgents.find(a => a.id === agentId);
        if (customAgent) {
          const baseAgent = BASE_AGENTS.find(b => b.id === customAgent.base_agent_type) || BASE_AGENTS[0];
          agent = { ...customAgent, icon: baseAgent.icon, color: baseAgent.color, isCustom: true };
        }
      }
    }
    if (!agent) return;
    const newNode = {
      id: `node-${Date.now()}`,
      agentId,
      agentName: agent.name,
      agentIcon: agent.icon,
      agentColor: agent.color,
      isCustomAgent: agent.isCustom || false,
      customConfig: agent.isCustom ? { system_prompt: agent.system_prompt, style: agent.style, extension: agent.extension } : null,
      x,
      y,
      instructions: '',
      label: '',
      documents: [],
      output: null,
      status: 'pending'
    };
    setWorkflowNodes(prev => [...prev, newNode]);
    setSelectedNode(newNode.id);
  };

  const updateWorkflowNode = (nodeId, updates) => {
    setWorkflowNodes(prev => prev.map(node =>
      node.id === nodeId ? { ...node, ...updates } : node
    ));
  };

  const removeWorkflowNode = (nodeId) => {
    setWorkflowNodes(prev => prev.filter(node => node.id !== nodeId));
    setWorkflowConnections(prev => prev.filter(conn =>
      conn.sourceId !== nodeId && conn.targetId !== nodeId
    ));
    if (selectedNode === nodeId) setSelectedNode(null);
  };

  const addWorkflowConnection = (sourceId, targetId) => {
    if (sourceId === targetId) return;
    const exists = workflowConnections.some(
      conn => conn.sourceId === sourceId && conn.targetId === targetId
    );
    if (exists) return;
    setWorkflowConnections(prev => [...prev, {
      id: `conn-${Date.now()}`,
      sourceId,
      targetId
    }]);
  };

  const removeWorkflowConnection = (connId) => {
    setWorkflowConnections(prev => prev.filter(conn => conn.id !== connId));
  };

  const getWorkflowGroups = () => {
    const visited = new Set();
    const groups = [];

    const findConnectedNodes = (nodeId, group) => {
      if (visited.has(nodeId)) return;
      visited.add(nodeId);
      group.push(nodeId);
      workflowConnections.forEach(conn => {
        if (conn.sourceId === nodeId) findConnectedNodes(conn.targetId, group);
        if (conn.targetId === nodeId) findConnectedNodes(conn.sourceId, group);
      });
    };

    workflowNodes.forEach(node => {
      if (!visited.has(node.id)) {
        const group = [];
        findConnectedNodes(node.id, group);
        groups.push(group);
      }
    });

    return groups;
  };

  const getNodeConnections = (nodeId) => {
    const incoming = workflowConnections.filter(c => c.targetId === nodeId);
    const outgoing = workflowConnections.filter(c => c.sourceId === nodeId);
    return { incoming, outgoing };
  };

  const getOutputGroups = () => {
    const nodesWithOutput = workflowNodes.filter(n => n.output);
    if (nodesWithOutput.length === 0) return [];

    const groups = getWorkflowGroups();
    const outputGroups = [];

    groups.forEach((groupNodeIds, idx) => {
      const groupNodes = groupNodeIds
        .map(id => workflowNodes.find(n => n.id === id))
        .filter(n => n && n.output);

      if (groupNodes.length === 0) return;

      const sortedNodes = [];
      const remaining = [...groupNodes];

      const startNodes = groupNodes.filter(n =>
        !workflowConnections.some(c => c.targetId === n.id && groupNodeIds.includes(c.sourceId))
      );

      if (startNodes.length > 0) {
        let current = startNodes[0];
        while (current && remaining.length > 0) {
          sortedNodes.push(current);
          remaining.splice(remaining.indexOf(current), 1);
          const nextConn = workflowConnections.find(c => c.sourceId === current.id);
          current = nextConn ? remaining.find(n => n.id === nextConn.targetId) : null;
        }
        sortedNodes.push(...remaining);
      } else {
        sortedNodes.push(...groupNodes);
      }

      outputGroups.push({
        id: `group-${idx}`,
        isWorkflow: sortedNodes.length > 1,
        nodes: sortedNodes,
        label: sortedNodes.length > 1
          ? sortedNodes.map(n => n.agentName).join(' → ')
          : sortedNodes[0].agentName,
        icons: sortedNodes.map(n => n.agentIcon),
        colors: sortedNodes.map(n => n.agentColor)
      });
    });

    return outputGroups;
  };

  // State - Estadisticas
  const [stats, setStats] = useState({ tokens: 0, tasks: 0, time: 0 });
  const [sessionTime, setSessionTime] = useState(0);
  const [totalSessionTime, setTotalSessionTime] = useState(0);
  const [timeEntries, setTimeEntries] = useState([]);
  const [showTimeHistory, setShowTimeHistory] = useState(false);
  const sessionStartRef = useRef(null);

  // State - Toast notifications
  const [toasts, setToasts] = useState([]);

  const showToast = (message, type = 'info') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3000);
  };

  useEffect(() => {
    const savedProject = localStorage.getItem('currentProject');
    if (savedProject) {
      const project = JSON.parse(savedProject);
      setCurrentProject(project);
    }
    fetchProjects(!!savedProject);
    fetchCustomAgents();
  }, []);

  const prevProjectRef = useRef(null);

  useEffect(() => {
    if (currentProject) {
      // Guardar referencia a la sesión activa del proyecto anterior
      if (prevProjectRef.current && prevProjectRef.current.id !== currentProject.id) {
        localStorage.setItem(`projectSession_${prevProjectRef.current.id}`, currentSession || '');
      }

      // Cargar última sesión del nuevo proyecto
      const savedSession = localStorage.getItem(`projectSession_${currentProject.id}`);
      setCurrentSession(savedSession || null);

      prevProjectRef.current = currentProject;
      setCurrentFolder(null);
      setFolderPath([]);
      fetchDocuments();
      fetchSessions();
      localStorage.setItem('currentProject', JSON.stringify(currentProject));
    }
  }, [currentProject]);

  const prevSessionRef = useRef(null);
  const prevSessionTimeRef = useRef(0);

  useEffect(() => {
    if (currentSession) {
      // Guardar tiempo de la sesión anterior en la BD
      if (prevSessionRef.current && prevSessionRef.current !== currentSession && prevSessionTimeRef.current > 5) {
        saveTimeEntry(prevSessionRef.current, prevSessionTimeRef.current);
      }

      // Limpiar estado antes de cargar la nueva sesión
      setWorkflowNodes([]);
      setWorkflowConnections([]);
      setWorkflowExecutions([]);
      setActiveExecutionId(null);
      setCompletedTasks([]);
      setStats({ tokens: 0, tasks: 0, time: 0 });
      setSessionTime(0);

      // Inicializar tiempo de sesión actual
      sessionStartRef.current = new Date();

      // Cargar desde BD (loadSessionVersions carga todo: workflow_state + versions)
      loadTimeData(currentSession);
      loadSessionVersions(currentSession);

      prevSessionRef.current = currentSession;
      prevSessionTimeRef.current = 0;
    } else {
      // Guardar tiempo de la sesión anterior
      if (prevSessionRef.current && prevSessionTimeRef.current > 5) {
        saveTimeEntry(prevSessionRef.current, prevSessionTimeRef.current);
      }
      prevSessionRef.current = null;
      prevSessionTimeRef.current = 0;
      sessionStartRef.current = null;
      setWorkflowExecutions([]);
      setActiveExecutionId(null);
      setWorkflowNodes([]);
      setWorkflowConnections([]);
      setCompletedTasks([]);
      setStats({ tokens: 0, tasks: 0, time: 0 });
      setSessionTime(0);
      setTotalSessionTime(0);
      setTimeEntries([]);
    }
  }, [currentSession]);

  useEffect(() => {
    if (documents.some(d => d.status === 'pending' || d.status === 'processing')) {
      const interval = setInterval(fetchDocuments, 5000);
      return () => clearInterval(interval);
    }
  }, [documents]);

  // Contador de tiempo de sesión
  useEffect(() => {
    if (!currentSession) return;
    const interval = setInterval(() => {
      setSessionTime(prev => {
        const newTime = prev + 1;
        prevSessionTimeRef.current = newTime;
        return newTime;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [currentSession]);

  // Guardar tiempo al cerrar/recargar pestaña
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (currentSession && prevSessionTimeRef.current > 5) {
        const payload = JSON.stringify({
          started_at: sessionStartRef.current?.toISOString() || new Date(Date.now() - prevSessionTimeRef.current * 1000).toISOString(),
          ended_at: new Date().toISOString(),
          duration_seconds: prevSessionTimeRef.current
        });
        navigator.sendBeacon(
          `/api/v1/conversations/${currentSession}/time-entries`,
          new Blob([payload], { type: 'application/json' })
        );
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [currentSession]);

  // Ocultar menú de selección al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (selectionMenu.visible && !selectionMenu.showInput) {
        const menuEl = document.querySelector('.selection-menu');
        if (menuEl && !menuEl.contains(e.target)) {
          setSelectionMenu(prev => ({ ...prev, visible: false }));
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectionMenu.visible, selectionMenu.showInput]);

  const fetchProjects = async (hasSavedProject = false) => {
    try {
      const response = await api.get('/projects/');
      setProjects(response.data);
      if (!hasSavedProject && !currentProject && response.data.length > 0) {
        setCurrentProject(response.data[0]);
      }
    } catch (err) {
      console.error('Error fetching projects:', err);
    }
  };

  const fetchDocuments = async () => {
    if (!currentProject) return;
    try {
      const response = await api.get(`/projects/${currentProject.id}/documents`);
      setDocuments(response.data);
    } catch (err) {
      console.error('Error fetching documents:', err);
    }
  };

  const fetchSessions = async () => {
    if (!currentProject) return;
    try {
      const response = await api.get(`/conversations/project/${currentProject.id}`);
      setSessions(response.data);
    } catch (err) {
      console.error('Error fetching sessions:', err);
    }
  };

  const fetchCustomAgents = async () => {
    try {
      const response = await api.get('/agents/definitions');
      const custom = response.data.filter(a => !a.is_builtin);
      setCustomAgents(custom);
    } catch (err) {
      console.error('Error fetching agents:', err);
    }
  };

  const openAgentModal = (agent = null) => {
    if (agent) {
      setEditingAgent(agent);
      setAgentForm({
        name: agent.name,
        base_agent_type: agent.base_agent_type || 'writer',
        style: agent.style || 'formal',
        extension: agent.extension || 'balanced',
        system_prompt: agent.system_prompt || ''
      });
    } else {
      setEditingAgent(null);
      setAgentForm({
        name: '',
        base_agent_type: 'writer',
        style: 'formal',
        extension: 'balanced',
        system_prompt: ''
      });
    }
    setShowAgentModal(true);
  };

  const saveAgent = async () => {
    if (!agentForm.name.trim()) return;
    try {
      if (editingAgent) {
        await api.put(`/agents/definitions/${editingAgent.id}`, {
          name: agentForm.name,
          system_prompt: agentForm.system_prompt,
          style: agentForm.style,
          extension: agentForm.extension
        });
      } else {
        await api.post('/agents/definitions', {
          name: agentForm.name,
          type: agentForm.base_agent_type,
          system_prompt: agentForm.system_prompt,
          style: agentForm.style,
          extension: agentForm.extension,
          base_agent_type: agentForm.base_agent_type
        });
      }
      setShowAgentModal(false);
      fetchCustomAgents();
    } catch (err) {
      console.error('Error saving agent:', err);
    }
  };

  const deleteAgent = async (agentId) => {
    if (!window.confirm('¿Eliminar este agente?')) return;
    try {
      await api.delete(`/agents/definitions/${agentId}`);
      fetchCustomAgents();
    } catch (err) {
      console.error('Error deleting agent:', err);
    }
  };

  const loadTimeData = async (conversationId) => {
    if (!conversationId || conversationId.startsWith('local-')) return;
    try {
      const [totalResponse, entriesResponse] = await Promise.all([
        api.get(`/conversations/${conversationId}/total-time`),
        api.get(`/conversations/${conversationId}/time-entries`)
      ]);
      setTotalSessionTime(totalResponse.data.total_time_seconds || 0);
      setTimeEntries(entriesResponse.data || []);
    } catch (err) {
      console.error('Error loading time data:', err);
    }
  };

  const loadSessionVersions = async (conversationId) => {
    if (!conversationId || conversationId.startsWith('local-')) return;
    try {
      const [conversationResponse, versionsResponse] = await Promise.all([
        api.get(`/conversations/${conversationId}`),
        api.get(`/conversations/${conversationId}/versions`)
      ]);

      const conversation = conversationResponse.data;
      const versions = versionsResponse.data || [];
      const workflowState = conversation.workflow_state || {};

      const formattedVersions = versions.map(v => ({
        id: v.id,
        content: v.content,
        label: v.label,
        timestamp: new Date(v.created_at).toLocaleTimeString()
      }));

      if (workflowState.nodes && workflowState.nodes.length > 0) {
        setWorkflowNodes(workflowState.nodes);
        setWorkflowConnections(workflowState.connections || []);
      }

      setWorkflowExecutions(prev => {
        const existing = prev.find(e => e.id === conversationId);
        if (existing) {
          return prev.map(e => e.id === conversationId
            ? {
                ...e,
                versions: formattedVersions,
                currentVersionIndex: formattedVersions.length - 1,
                nodes: workflowState.nodes || e.nodes,
                connections: workflowState.connections || e.connections,
                icons: workflowState.icons || e.icons,
                colors: workflowState.colors || e.colors
              }
            : e
          );
        } else {
          return [...prev, {
            id: conversationId,
            label: conversation.title || 'Sesión restaurada',
            icons: workflowState.icons || [],
            colors: workflowState.colors || [],
            nodes: workflowState.nodes || [],
            connections: workflowState.connections || [],
            versions: formattedVersions,
            currentVersionIndex: formattedVersions.length - 1,
            createdAt: conversation.created_at || new Date().toISOString()
          }];
        }
      });
      setActiveExecutionId(conversationId);

    } catch (err) {
      console.error('Error loading session versions:', err);
    }
  };

  const saveTimeEntry = async (conversationId, duration) => {
    if (!conversationId || duration < 5 || conversationId.startsWith('local-')) return;
    try {
      const startedAt = sessionStartRef.current || new Date(Date.now() - duration * 1000);
      await api.post(`/conversations/${conversationId}/time-entries`, {
        started_at: startedAt.toISOString(),
        ended_at: new Date().toISOString(),
        duration_seconds: duration
      });
    } catch (err) {
      console.error('Error saving time entry:', err);
    }
  };

  const renameSession = async (sessionId, newTitle) => {
    if (!newTitle.trim()) {
      setEditingSessionId(null);
      return;
    }
    try {
      await api.put(`/conversations/${sessionId}`, { title: newTitle.trim() });
      setSessions(prev => prev.map(s => s.id === sessionId ? { ...s, title: newTitle.trim() } : s));
    } catch (err) {
      console.error('Error renaming session:', err);
    }
    setEditingSessionId(null);
  };

  const deleteSession = async (sessionId) => {
    if (!window.confirm('¿Eliminar esta sesión?')) return;
    try {
      await api.delete(`/conversations/${sessionId}`);
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      localStorage.removeItem(`sessionState_${sessionId}`);
      if (currentSession === sessionId) {
        setCurrentSession(null);
      }
    } catch (err) {
      console.error('Error deleting session:', err);
    }
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !currentProject) return;
    setUploadLoading(true);
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      try {
        await api.post(`/projects/${currentProject.id}/documents`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } catch (err) {
        console.error('Error uploading:', err);
      }
    }
    setUploadLoading(false);
    await fetchDocuments();
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Eliminar este documento?')) return;
    try {
      await api.delete(`/projects/${currentProject.id}/documents/${docId}`);
      setSelectedDocs(prev => prev.filter(id => id !== docId));
      if (templateDoc === docId) setTemplateDoc(null);
      await fetchDocuments();
    } catch (err) {
      console.error('Error deleting:', err);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const toggleDocSelection = (docId) => {
    setSelectedDocs(prev =>
      prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]
    );
  };

  const setAsTemplate = (docId) => {
    setTemplateDoc(templateDoc === docId ? null : docId);
  };

  const executeWorkflow = async () => {
    const nodesWithoutDocs = workflowNodes.some(n => !n.documents || n.documents.length === 0);
    if (workflowNodes.length === 0 || isExecuting || nodesWithoutDocs) return;

    setIsExecuting(true);
    setExecutionProgress(0);
    setActiveTab('editor');

    // Crear nueva ejecución para este workflow (también crea sesión en BD)
    const executionId = await createWorkflowExecution(workflowNodes, workflowConnections);

    const groups = getWorkflowGroups();
    let allOutputs = [];

    const getExecutionOrder = (groupNodeIds) => {
      const nodes = groupNodeIds.map(id => workflowNodes.find(n => n.id === id));
      const inDegree = {};
      groupNodeIds.forEach(id => { inDegree[id] = 0; });
      workflowConnections.forEach(conn => {
        if (groupNodeIds.includes(conn.targetId)) {
          inDegree[conn.targetId]++;
        }
      });
      const sorted = [];
      const queue = groupNodeIds.filter(id => inDegree[id] === 0);
      while (queue.length > 0) {
        const nodeId = queue.shift();
        sorted.push(nodeId);
        workflowConnections.forEach(conn => {
          if (conn.sourceId === nodeId && groupNodeIds.includes(conn.targetId)) {
            inDegree[conn.targetId]--;
            if (inDegree[conn.targetId] === 0) {
              queue.push(conn.targetId);
            }
          }
        });
      }
      return sorted.map(id => nodes.find(n => n.id === id));
    };

    const executeNode = async (node, previousOutput = null) => {
      const startTime = Date.now();
      updateWorkflowNode(node.id, { status: 'running' });
      setExecutionStatus(`${node.agentIcon} ${node.agentName}: ${node.label || 'Procesando...'}`);

      const agentEntry = {
        id: node.id,
        icon: node.agentIcon,
        name: node.agentName,
        task: node.label || node.instructions.slice(0, 50),
        status: 'Procesando...',
        progress: 10,
        startTime,
        tokens: 0
      };
      setActiveAgents(prev => [...prev, agentEntry]);
      setExecutionProgress(10);

      try {
        let customPrompt = node.instructions;
        if (previousOutput) {
          customPrompt = `Contenido previo:\n${previousOutput}\n\nInstrucciones:\n${node.instructions}`;
        }

        setActiveAgents(prev => prev.map(a => a.id === node.id ? { ...a, status: 'Analizando...', progress: 30 } : a));
        setExecutionProgress(30);

        const response = await api.post('/analysis/', {
          project_id: currentProject.id,
          filter_id: 'custom',
          custom_prompt: customPrompt,
          selected_documents: node.documents
        });

        setActiveAgents(prev => prev.map(a => a.id === node.id ? { ...a, status: 'Completado', progress: 100 } : a));
        setExecutionProgress(100);

        const nodeOutput = response.data.content || '';
        updateWorkflowNode(node.id, { status: 'completed', output: nodeOutput });

        setActiveAgents(prev => prev.filter(a => a.id !== node.id));
        setCompletedTasks(prev => [...prev, {
          label: node.label || node.agentName,
          tokens: 0,
          agentIcon: node.agentIcon
        }]);
        setStats(prev => ({
          tokens: prev.tokens,
          tasks: prev.tasks + 1,
          time: prev.time + Math.floor((Date.now() - startTime) / 1000)
        }));

        return nodeOutput;

      } catch (err) {
        console.error('Error executing node:', err);
        updateWorkflowNode(node.id, { status: 'error', output: `Error: ${err.message}` });
        setActiveAgents(prev => prev.filter(a => a.id !== node.id));
        return null;
      }
    };

    const executeGroup = async (groupNodeIds) => {
      const orderedNodes = getExecutionOrder(groupNodeIds);
      let previousOutput = null;
      const groupOutputs = [];

      for (const node of orderedNodes) {
        const output = await executeNode(node, previousOutput);
        if (output) {
          groupOutputs.push({ node, output });
          previousOutput = output;
        }
      }
      return groupOutputs;
    };

    try {
      const totalNodes = workflowNodes.length;
      let completedNodes = 0;

      const groupPromises = groups.map(async (groupNodeIds) => {
        const outputs = await executeGroup(groupNodeIds);
        completedNodes += groupNodeIds.length;
        setExecutionProgress(Math.round((completedNodes / totalNodes) * 100));
        return outputs;
      });

      const groupResults = await Promise.all(groupPromises);

      groupResults.forEach((groupOutputs, groupIndex) => {
        if (groupOutputs.length > 0) {
          allOutputs.push({
            groupIndex,
            nodes: groupOutputs
          });
        }
      });

      let finalContent = '';
      allOutputs.forEach((group, i) => {
        if (group.nodes.length === 1) {
          const { node, output } = group.nodes[0];
          finalContent += `## ${node.label || node.agentName}\n\n${output}\n\n`;
        } else {
          finalContent += `## Grupo ${i + 1}\n\n`;
          group.nodes.forEach(({ node, output }) => {
            finalContent += `### ${node.label || node.agentName}\n\n${output}\n\n`;
          });
        }
      });

      const trimmedContent = finalContent.trim();
      setExecutionProgress(100);
      setExecutionStatus('Workflow completado');
      setActiveAgents([]);

      // Guardar versión inicial en la ejecución (y en BD)
      if (trimmedContent) {
        await addVersionToExecution(executionId, trimmedContent, 'Generación inicial');
      }

      showToast('Workflow ejecutado correctamente', 'success');
      await fetchSessions();

    } catch (err) {
      console.error('Error executing workflow:', err);
      setExecutionStatus('Error en el workflow');
      showToast('Error al ejecutar el workflow', 'error');
    } finally {
      setIsExecuting(false);
    }
  };

  const [isRefining, setIsRefining] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editableContent, setEditableContent] = useState('');

  const startEditing = () => {
    setEditableContent(currentOutput);
    setIsEditing(true);
  };

  const saveEditing = async () => {
    setIsEditing(false);
    setSelectionMenu(prev => ({ ...prev, visible: false }));
    if (editableContent !== currentOutput && activeExecutionId) {
      await addVersionToExecution(activeExecutionId, editableContent, 'Edicion manual');
    }
    showToast('Cambios guardados', 'success');
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditableContent('');
    setSelectionMenu(prev => ({ ...prev, visible: false }));
  };

  const handleRefine = async (action, customInstructions = null) => {
    if (!currentOutput || isRefining || !currentProject) return;

    const actionLabels = {
      extend: 'Extender',
      shorten: 'Acortar',
      regenerate: 'Regenerar'
    };

    const actionPrompts = {
      extend: `Extiende y amplía el siguiente contenido con más detalles, ejemplos y explicaciones. Mantén el mismo formato y estilo:\n\n${currentOutput}`,
      shorten: `Resume y acorta el siguiente contenido manteniendo la información más importante. Mantén el mismo formato:\n\n${currentOutput}`,
      regenerate: `Reescribe el siguiente contenido con un enfoque diferente pero manteniendo la misma información esencial:\n\n${currentOutput}`
    };

    setIsRefining(true);
    setExecutionProgress(0);
    setExecutionStatus(`Refinando: ${actionLabels[action] || action}...`);

    const startTime = Date.now();
    setActiveAgents([{
      id: 'refiner',
      name: 'Refinador',
      icon: '✨',
      task: actionLabels[action] || action,
      progress: 0,
      status: 'Procesando...',
      tokens: 0,
      startTime
    }]);

    try {
      setExecutionProgress(30);
      setActiveAgents(prev => prev.map(a => ({ ...a, progress: 30, status: 'Analizando...' })));

      const customPrompt = customInstructions
        ? `${customInstructions}\n\nContenido actual:\n${currentOutput}`
        : actionPrompts[action];

      const response = await api.post('/analysis/', {
        project_id: currentProject.id,
        filter_id: 'custom',
        custom_prompt: customPrompt,
        selected_documents: selectedDocs
      });

      setExecutionProgress(100);
      setActiveAgents(prev => prev.map(a => ({ ...a, progress: 100, status: 'Completado' })));

      const refinedContent = response.data.content || '';
      setExecutionStatus('Refinamiento completado');

      if (activeExecutionId && refinedContent) {
        await addVersionToExecution(activeExecutionId, refinedContent, actionLabels[action] || action);
      }
      showToast('Contenido refinado correctamente', 'success');
    } catch (err) {
      console.error('Error refining:', err);
      setExecutionStatus('Error al refinar');
      showToast('Error al refinar el contenido', 'error');
    } finally {
      setIsRefining(false);
      setActiveAgents([]);
    }
  };

  const copyToClipboard = (format) => {
    const text = format === 'md' ? currentOutput : currentOutput.replace(/[#*_`]/g, '');
    navigator.clipboard.writeText(text);
    showToast(`Copiado al portapapeles (${format === 'md' ? 'Markdown' : 'texto plano'})`, 'success');
  };

  const handleTextSelection = () => {
    const textarea = editTextareaRef.current;
    if (!textarea) return;

    const { selectionStart, selectionEnd, value } = textarea;
    const selectedText = value.substring(selectionStart, selectionEnd).trim();

    if (selectedText.length > 0) {
      const rect = textarea.getBoundingClientRect();

      setSelectionMenu({
        visible: true,
        x: rect.left + rect.width / 2,
        y: rect.top + 50,
        text: selectedText,
        selectionStart,
        selectionEnd,
        showInput: false,
        inputMode: null,
        inputValue: ''
      });
    }
  };

  const handleSelectionAction = async (action) => {
    if (!selectionMenu.text || !currentProject) return;

    if (action === 'edit') {
      setSelectionMenu(prev => ({ ...prev, showInput: true, inputMode: 'edit' }));
      return;
    }

    if (action === 'explain') {
      setSelectionMenu(prev => ({ ...prev, showInput: true, inputMode: 'explain' }));
      return;
    }

    if (action === 'copy') {
      navigator.clipboard.writeText(selectionMenu.text);
      showToast('Texto copiado al portapapeles', 'success');
      setSelectionMenu(prev => ({ ...prev, visible: false }));
      return;
    }
  };

  const executeSelectionAction = async () => {
    if (!selectionMenu.text || !currentProject || selectionLoading) return;

    const { text, inputMode, inputValue } = selectionMenu;
    setSelectionLoading(true);

    try {
      let prompt = '';

      if (inputMode === 'edit') {
        prompt = `Modifica el siguiente texto segun estas instrucciones: "${inputValue}"\n\nTexto a modificar:\n${text}\n\nDevuelve SOLO el texto modificado, sin explicaciones adicionales.`;
      } else if (inputMode === 'explain') {
        const question = inputValue.trim() || 'Explica este texto';
        prompt = `${question}\n\nTexto de referencia:\n${text}`;
      }

      const response = await api.post('/analysis/', {
        project_id: currentProject.id,
        filter_id: 'custom',
        custom_prompt: prompt,
        selected_documents: selectedDocs
      });

      const resultContent = response.data.content || '';

      setSelectionMenu(prev => ({
        ...prev,
        showInput: false,
        result: resultContent,
        showResult: true
      }));
    } catch (err) {
      console.error('Error executing selection action:', err);
      showToast('Error al procesar la accion', 'error');
    } finally {
      setSelectionLoading(false);
    }
  };

  const applySelectionResult = (action) => {
    const { selectionStart, selectionEnd, result } = selectionMenu;

    if (action === 'replace') {
      const before = editableContent.substring(0, selectionStart);
      const after = editableContent.substring(selectionEnd);
      setEditableContent(before + result + after);
      showToast('Texto reemplazado', 'success');
    } else if (action === 'insert') {
      const before = editableContent.substring(0, selectionEnd);
      const after = editableContent.substring(selectionEnd);
      setEditableContent(before + '\n\n' + result + after);
      showToast('Texto insertado', 'success');
    } else if (action === 'copy') {
      navigator.clipboard.writeText(result);
      showToast('Copiado al portapapeles', 'success');
      return;
    }

    closeSelectionMenu();
  };

  const closeSelectionMenu = () => {
    setSelectionMenu({
      visible: false, x: 0, y: 0, text: '', selectionStart: 0, selectionEnd: 0,
      showInput: false, inputMode: null, inputValue: '', result: null, showResult: false
    });
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim() || !currentProject || chatLoading) return;

    const userMessage = { role: 'user', content: chatInput.trim() };
    setChatMessages(prev => [...prev, userMessage]);
    setChatInput('');
    setChatLoading(true);

    try {
      const response = await api.post(`/analysis/rag/chat-simple?project_id=${currentProject.id}`, {
        query: userMessage.content,
        document_ids: chatFilterDocs.length > 0 ? chatFilterDocs : null,
        conversation_history: chatMessages.slice(-10)
      });

      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        sources: response.data.sources
      };
      setChatMessages(prev => [...prev, assistantMessage]);

      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (err) {
      console.error('Error in chat:', err);
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Error al procesar tu pregunta. Intenta de nuevo.',
        error: true
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  const toggleChatDocFilter = (docId) => {
    setChatFilterDocs(prev =>
      prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]
    );
  };

  const clearChat = () => {
    setChatMessages([]);
    setChatFilterDocs([]);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getDocStatus = (doc) => {
    if (doc.status === 'completed') return { icon: <Check size={12} />, class: 'ready', text: '' };
    if (doc.status === 'error') return { icon: <X size={12} />, class: 'error', text: 'Error' };
    return { icon: <Loader size={12} className="spin" />, class: 'processing', text: '' };
  };

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop()?.toLowerCase() || '';
    const icons = {
      pdf: '📕',
      docx: '📘', doc: '📘',
      xlsx: '📗', xls: '📗',
      pptx: '📙', ppt: '📙',
      txt: '📄',
      jpg: '🖼️', jpeg: '🖼️', png: '🖼️', gif: '🖼️', bmp: '🖼️'
    };
    return icons[ext] || '📄';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getSortedDocuments = () => {
    const filtered = documents.filter(d => d.parent_id === currentFolder);
    return filtered.sort((a, b) => {
      if (a.is_folder !== b.is_folder) return a.is_folder ? -1 : 1;
      let comparison = 0;
      if (sortBy === 'name') {
        comparison = a.filename.localeCompare(b.filename);
      } else if (sortBy === 'date') {
        comparison = new Date(a.processed_at || a.created_at || 0) - new Date(b.processed_at || b.created_at || 0);
      } else if (sortBy === 'status') {
        comparison = (a.status || '').localeCompare(b.status || '');
      } else if (sortBy === 'size') {
        comparison = (a.file_size || 0) - (b.file_size || 0);
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });
  };

  const toggleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const navigateToFolder = (folderId) => {
    if (folderId) {
      const folder = documents.find(d => d.id === folderId);
      if (folder) {
        setFolderPath(prev => [...prev, { id: folder.id, name: folder.filename }]);
      }
    }
    setCurrentFolder(folderId);
    setSelectedDocs([]);
  };

  const navigateUp = () => {
    if (folderPath.length > 0) {
      const newPath = [...folderPath];
      newPath.pop();
      setFolderPath(newPath);
      setCurrentFolder(newPath.length > 0 ? newPath[newPath.length - 1].id : null);
      setSelectedDocs([]);
    }
  };

  const navigateToPathIndex = (index) => {
    if (index < 0) {
      setFolderPath([]);
      setCurrentFolder(null);
    } else {
      const newPath = folderPath.slice(0, index + 1);
      setFolderPath(newPath);
      setCurrentFolder(newPath[index].id);
    }
    setSelectedDocs([]);
  };

  const createFolder = async () => {
    const name = window.prompt('Nombre de la carpeta:');
    if (!name || !name.trim()) return;
    try {
      await api.post(`/projects/${currentProject.id}/folders?folder_name=${encodeURIComponent(name.trim())}&parent_id=${currentFolder || ''}`);
      fetchDocuments();
      showToast('Carpeta creada', 'success');
    } catch (err) {
      showToast('Error al crear carpeta', 'error');
    }
  };

  const moveDocumentToFolder = async (docId, targetFolderId) => {
    try {
      await api.put(`/projects/${currentProject.id}/documents/${docId}/move?parent_id=${targetFolderId || ''}`);
      fetchDocuments();
    } catch (err) {
      showToast('Error al mover documento', 'error');
    }
  };

  return (
    <div className="workspace">
      {/* SIDEBAR IZQUIERDA */}
      <aside className="sidebar-left">
        <div className="sidebar-logo">
          <FileText size={20} />
          <span>Document Analyzer</span>
        </div>

        <div className="sidebar-sections">
        {/* Proyectos */}
        <div className="sidebar-section">
          <button className="section-header" onClick={() => toggleSection('projects')}>
            {expandedSections.projects ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            <FolderOpen size={16} />
            <span>Proyectos</span>
          </button>
          {expandedSections.projects && (
            <div className="section-content">
              {projects.map(project => (
                <button
                  key={project.id}
                  className={`sidebar-item ${currentProject?.id === project.id ? 'active' : ''}`}
                  onClick={() => setCurrentProject(project)}
                >
                  <span className="item-dot" />
                  <span className="item-text">{project.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Sesiones */}
        <div className="sidebar-section">
          <button className="section-header" onClick={() => toggleSection('sessions')}>
            {expandedSections.sessions ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            <MessageSquare size={16} />
            <span>Sesiones ({sessions.length})</span>
          </button>
          {expandedSections.sessions && (
            <div className="section-content">
              {sessions.map(session => (
                <div
                  key={session.id}
                  className={`sidebar-item ${currentSession === session.id ? 'active' : ''}`}
                  onClick={() => editingSessionId !== session.id && setCurrentSession(session.id)}
                >
                  <span className="item-dot" />
                  {editingSessionId === session.id ? (
                    <input
                      type="text"
                      className="session-edit-input"
                      value={editingSessionTitle}
                      onChange={(e) => setEditingSessionTitle(e.target.value)}
                      onBlur={() => renameSession(session.id, editingSessionTitle)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') renameSession(session.id, editingSessionTitle);
                        if (e.key === 'Escape') setEditingSessionId(null);
                      }}
                      autoFocus
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <>
                      <span className="item-text">{session.title}</span>
                      <div className="session-actions">
                        <button
                          className="session-btn edit"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingSessionId(session.id);
                            setEditingSessionTitle(session.title);
                          }}
                          title="Renombrar"
                        >
                          <Edit3 size={12} />
                        </button>
                        <button
                          className="session-btn delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteSession(session.id);
                          }}
                          title="Eliminar"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
              <button
                className="sidebar-item new-item"
                onClick={() => setCurrentSession(null)}
              >
                <Plus size={14} />
                <span>Nueva sesion</span>
              </button>
            </div>
          )}
        </div>

        {/* Agentes */}
        <div className="sidebar-section">
          <button className="section-header" onClick={() => toggleSection('agents')}>
            {expandedSections.agents ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            <Bot size={16} />
            <span>Agentes</span>
            <span className="section-count">{BASE_AGENTS.length + customAgents.length}</span>
          </button>
          {expandedSections.agents && (
            <div className="section-content">
              {/* Agentes base */}
              {BASE_AGENTS.map(agent => (
                <div
                  key={agent.id}
                  className="sidebar-item agent-item draggable-agent"
                  style={{ '--agent-color': agent.color }}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData('agentId', agent.id);
                    e.dataTransfer.setData('agentData', JSON.stringify(agent));
                    e.dataTransfer.effectAllowed = 'copy';
                  }}
                >
                  <span className="agent-icon">{agent.icon}</span>
                  <span className="item-text">{agent.name}</span>
                </div>
              ))}
              {/* Agentes personalizados */}
              {customAgents.length > 0 && (
                <div className="custom-agents-divider">
                  <span>Personalizados</span>
                </div>
              )}
              {customAgents.map(agent => {
                const baseAgent = BASE_AGENTS.find(b => b.id === agent.base_agent_type) || BASE_AGENTS[0];
                return (
                  <div
                    key={agent.id}
                    className="sidebar-item agent-item draggable-agent custom"
                    style={{ '--agent-color': baseAgent.color }}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData('agentId', agent.id);
                      e.dataTransfer.setData('agentData', JSON.stringify({
                        ...agent,
                        icon: baseAgent.icon,
                        color: baseAgent.color,
                        isCustom: true
                      }));
                      e.dataTransfer.effectAllowed = 'copy';
                    }}
                  >
                    <span className="agent-icon">{baseAgent.icon}</span>
                    <span className="item-text">{agent.name}</span>
                    <span className="custom-badge">*</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        </div>

        {/* Footer */}
        <div className="sidebar-footer">
          <button className="footer-btn" onClick={() => navigate('/projects')}>
            <RefreshCw size={16} />
            <span>Cambiar Proyecto</span>
          </button>
          <button className="footer-btn" onClick={handleLogout}>
            <LogOut size={16} />
            <span>Cerrar Sesion</span>
          </button>
          <div className="footer-brand">
            Made by Kuik! Inteligencia Artificial S.L.
          </div>
        </div>
      </aside>

      {/* AREA PRINCIPAL */}
      <main className="main-area">
        {/* Header con Tabs */}
        <div className="main-header">
          <div className="tabs">
            <button
              className={`tab ${activeTab === 'documents' ? 'active' : ''}`}
              onClick={() => setActiveTab('documents')}
            >
              <FileText size={16} />
              <span>Documentos</span>
              {documents.length > 0 && <span className="tab-count">{documents.length}</span>}
            </button>
            <button
              className={`tab ${activeTab === 'agents' ? 'active' : ''}`}
              onClick={() => setActiveTab('agents')}
            >
              <Bot size={16} />
              <span>Agentes</span>
              {customAgents.length > 0 && <span className="tab-count">{customAgents.length}</span>}
            </button>
            <button
              className={`tab ${activeTab === 'tasks' ? 'active' : ''}`}
              onClick={() => setActiveTab('tasks')}
            >
              <List size={16} />
              <span>Tareas</span>
            </button>
            <button
              className={`tab ${activeTab === 'editor' ? 'active' : ''}`}
              onClick={() => setActiveTab('editor')}
            >
              <Edit3 size={16} />
              <span>Editor</span>
              {currentOutput && <span className="tab-badge" />}
            </button>
            <button
              className={`tab ${activeTab === 'preview' ? 'active' : ''}`}
              onClick={() => setActiveTab('preview')}
            >
              <FileCheck size={16} />
              <span>Preview</span>
            </button>
            <button
              className={`tab ${activeTab === 'chat' ? 'active' : ''}`}
              onClick={() => setActiveTab('chat')}
            >
              <MessageSquare size={16} />
              <span>Chat</span>
              {chatMessages.length > 0 && <span className="tab-badge">{chatMessages.length}</span>}
            </button>
          </div>
          <div className="header-actions">
            {currentProject && (
              <span className="project-badge">
                <FolderOpen size={14} />
                Proyecto: {currentProject.name}
              </span>
            )}
          </div>
        </div>

        {/* Contenido segun tab activo */}
        <div className="main-content">
          {activeTab === 'tasks' && (
            <div style={{ width: '100%', height: 'calc(100vh - 180px)' }}>
              <div style={{ width: '100%', height: '100%', display: 'flex', gap: '16px' }}>
                {/* Canvas de workflow */}
                <div
                  ref={canvasRef}
                  className="workflow-canvas"
                  style={{
                    flex: 1,
                    height: '100%',
                    backgroundColor: '#f8fafc',
                    backgroundImage: 'radial-gradient(circle, #cbd5e1 1px, transparent 1px)',
                    backgroundSize: '24px 24px',
                    border: '2px dashed #94a3b8',
                    borderRadius: '12px',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const agentId = e.dataTransfer.getData('agentId');
                    const agentDataStr = e.dataTransfer.getData('agentData');
                    const agentData = agentDataStr ? JSON.parse(agentDataStr) : null;
                    if (agentId && canvasRef.current) {
                      const rect = canvasRef.current.getBoundingClientRect();
                      const x = e.clientX - rect.left - 75;
                      const y = e.clientY - rect.top - 40;
                      addWorkflowNode(agentId, Math.max(0, x), Math.max(0, y), agentData);
                    }
                  }}
                  onClick={(e) => {
                    if (e.target === canvasRef.current) {
                      setSelectedNode(null);
                      setConnectingFrom(null);
                    }
                  }}
                >
                  {/* SVG para conexiones */}
                  {(workflowConnections.length > 0 || connectingFrom) && (
                    <svg className="workflow-connections">
                      <defs>
                        <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                          <polygon points="0 0, 10 3.5, 0 7" fill="#6366f1" />
                        </marker>
                      </defs>
                      {workflowConnections.map(conn => {
                        const sourceNode = workflowNodes.find(n => n.id === conn.sourceId);
                        const targetNode = workflowNodes.find(n => n.id === conn.targetId);
                        if (!sourceNode || !targetNode) return null;
                        const x1 = sourceNode.x + 150;
                        const y1 = sourceNode.y + 40;
                        const x2 = targetNode.x;
                        const y2 = targetNode.y + 40;
                        return (
                          <g key={conn.id} onClick={() => removeWorkflowConnection(conn.id)} style={{ cursor: 'pointer' }}>
                            <line
                              x1={x1} y1={y1} x2={x2} y2={y2}
                              stroke="#6366f1"
                              strokeWidth="2"
                              markerEnd="url(#arrowhead)"
                            />
                            <line
                              x1={x1} y1={y1} x2={x2} y2={y2}
                              stroke="transparent"
                              strokeWidth="20"
                            />
                          </g>
                        );
                      })}
                      {connectingFrom && (
                        <line
                          x1={workflowNodes.find(n => n.id === connectingFrom)?.x + 150 || 0}
                          y1={workflowNodes.find(n => n.id === connectingFrom)?.y + 40 || 0}
                          x2={workflowNodes.find(n => n.id === connectingFrom)?.x + 200 || 0}
                          y2={workflowNodes.find(n => n.id === connectingFrom)?.y + 40 || 0}
                          stroke="#6366f1"
                          strokeWidth="2"
                          strokeDasharray="5,5"
                        />
                      )}
                    </svg>
                  )}

                  {/* Nodos */}
                  {workflowNodes.map(node => (
                    <div
                      key={node.id}
                      className={`workflow-node ${selectedNode === node.id ? 'selected' : ''} ${node.status}`}
                      style={{
                        left: node.x,
                        top: node.y,
                        '--node-color': node.agentColor
                      }}
                      draggable
                      onDragStart={(e) => {
                        setDraggingNode(node.id);
                        e.dataTransfer.setData('nodeId', node.id);
                      }}
                      onDrag={(e) => {
                        if (e.clientX === 0 && e.clientY === 0) return;
                        const rect = canvasRef.current.getBoundingClientRect();
                        const x = e.clientX - rect.left - 75;
                        const y = e.clientY - rect.top - 40;
                        updateWorkflowNode(node.id, { x: Math.max(0, x), y: Math.max(0, y) });
                      }}
                      onDragEnd={() => setDraggingNode(null)}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (connectingFrom && connectingFrom !== node.id) {
                          addWorkflowConnection(connectingFrom, node.id);
                          setConnectingFrom(null);
                        } else {
                          setSelectedNode(node.id);
                        }
                      }}
                    >
                      <div className="node-header">
                        <span className="node-icon">{node.agentIcon}</span>
                        <span className="node-name">{node.agentName}</span>
                        <button
                          className="node-delete"
                          onClick={(e) => { e.stopPropagation(); removeWorkflowNode(node.id); }}
                        >
                          <X size={12} />
                        </button>
                      </div>
                      <div className="node-label">{node.label || 'Sin etiqueta'}</div>
                      <div className="node-connectors">
                        <div className="connector input" title="Entrada" />
                        <div
                          className="connector output"
                          title="Conectar a otro"
                          onClick={(e) => {
                            e.stopPropagation();
                            setConnectingFrom(connectingFrom === node.id ? null : node.id);
                          }}
                        />
                      </div>
                    </div>
                  ))}

                  {/* Empty state */}
                  {workflowNodes.length === 0 && (
                    <div className="workflow-empty">
                      <Bot size={48} />
                      <h3>Arrastra agentes aquí</h3>
                      <p>Crea un workflow arrastrando agentes desde la barra lateral</p>
                    </div>
                  )}
                </div>

                {/* Panel de configuración del nodo seleccionado */}
                {selectedNode && (() => {
                  const currentNode = workflowNodes.find(n => n.id === selectedNode);
                  const incomingConnections = workflowConnections.filter(c => c.targetId === selectedNode);
                  const isInitialNode = incomingConnections.length === 0;
                  const sourceNodes = incomingConnections.map(c => workflowNodes.find(n => n.id === c.sourceId)).filter(Boolean);

                  return (
                    <div className="node-config-panel">
                      <div className="config-header">
                        <span className="config-icon">{currentNode?.agentIcon}</span>
                        <span>{currentNode?.agentName}</span>
                        <span className={`node-type-badge ${isInitialNode ? 'initial' : 'chained'}`}>
                          {isInitialNode ? 'Nodo inicial' : 'Nodo encadenado'}
                        </span>
                      </div>
                      <div className="config-body">
                        {!isInitialNode && (
                          <div className="config-field input-source">
                            <label>Recibe input de:</label>
                            <div className="source-nodes">
                              {sourceNodes.map(source => (
                                <div key={source.id} className="source-node-tag">
                                  <span className="source-icon">{source.agentIcon}</span>
                                  <span>{source.label || source.agentName}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        <div className="config-field">
                          <label>Etiqueta</label>
                          <input
                            type="text"
                            value={currentNode?.label || ''}
                            onChange={(e) => updateWorkflowNode(selectedNode, { label: e.target.value })}
                            placeholder="Ej: Sección 1"
                          />
                        </div>
                        <div className="config-field">
                          <label>{isInitialNode ? 'Instrucciones' : 'Instrucciones de procesamiento'}</label>
                          <textarea
                            value={currentNode?.instructions || ''}
                            onChange={(e) => updateWorkflowNode(selectedNode, { instructions: e.target.value })}
                            placeholder={isInitialNode
                              ? "Instrucciones para este agente..."
                              : "Cómo procesar el contenido recibido del nodo anterior..."}
                            rows={6}
                          />
                        </div>
                        {isInitialNode && (
                          <>
                            <div className="config-field">
                              <label>Documentos fuente ({currentNode?.documents?.length || 0})</label>
                              <div className="config-docs-list">
                                {documents.filter(d => d.status === 'completed' && !d.is_folder).map(doc => {
                                  const nodeDocuments = currentNode?.documents || [];
                                  const isSelected = nodeDocuments.includes(doc.id);
                                  return (
                                    <div
                                      key={doc.id}
                                      className={`config-doc-item ${isSelected ? 'selected' : ''}`}
                                      onClick={() => {
                                        const currentDocs = currentNode?.documents || [];
                                        const newDocs = isSelected
                                          ? currentDocs.filter(id => id !== doc.id)
                                          : [...currentDocs, doc.id];
                                        updateWorkflowNode(selectedNode, { documents: newDocs });
                                      }}
                                      style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                                    >
                                      <input type="checkbox" checked={isSelected} readOnly style={{ width: '16px', flexShrink: 0 }} />
                                      <span style={{ color: '#1a1d21', flex: 1 }}>{doc.filename}</span>
                                    </div>
                                  );
                                })}
                                {documents.filter(d => d.status === 'completed' && !d.is_folder).length === 0 && (
                                  <div className="no-docs">No hay documentos procesados</div>
                                )}
                              </div>
                            </div>
                            <div className="config-field">
                              <label>Documentos especiales</label>
                              <div className="special-docs-options">
                                <div
                                  className={`special-doc-option ${!templateDoc ? 'unavailable' : ''} ${currentNode?.useTemplate ? 'active' : ''}`}
                                  onClick={() => templateDoc && updateWorkflowNode(selectedNode, { useTemplate: !currentNode?.useTemplate })}
                                >
                                  <input
                                    type="checkbox"
                                    checked={currentNode?.useTemplate || false}
                                    disabled={!templateDoc}
                                    readOnly
                                  />
                                  <div className="special-doc-info">
                                    <span className="special-doc-label">Usar documento modelo</span>
                                    <span className="special-doc-name">
                                      {templateDoc
                                        ? documents.find(d => d.id === templateDoc)?.filename || 'Modelo'
                                        : 'No configurado'}
                                    </span>
                                  </div>
                                </div>
                                <div
                                  className={`special-doc-option ${!instructionsDoc ? 'unavailable' : ''} ${currentNode?.useInstructions ? 'active' : ''}`}
                                  onClick={() => instructionsDoc && updateWorkflowNode(selectedNode, { useInstructions: !currentNode?.useInstructions })}
                                >
                                  <input
                                    type="checkbox"
                                    checked={currentNode?.useInstructions || false}
                                    disabled={!instructionsDoc}
                                    readOnly
                                  />
                                  <div className="special-doc-info">
                                    <span className="special-doc-label">Usar instrucciones</span>
                                    <span className="special-doc-name">
                                      {instructionsDoc
                                        ? documents.find(d => d.id === instructionsDoc)?.filename || 'Instrucciones'
                                        : 'No configurado'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Barra de acciones del workflow */}
              {workflowNodes.length > 0 && (() => {
                const initialNodes = workflowNodes.filter(n => !workflowConnections.some(c => c.targetId === n.id));
                const initialNodesMissingDocs = initialNodes.some(n => !n.documents || n.documents.length === 0);
                const anyNodeMissingInstructions = workflowNodes.some(n => !n.instructions.trim());

                return (
                <div className="workflow-actions">
                  <span className="workflow-info">
                    {workflowNodes.length} agente{workflowNodes.length !== 1 ? 's' : ''} · {workflowConnections.length} conexi{workflowConnections.length !== 1 ? 'ones' : 'ón'}
                    {initialNodesMissingDocs && <span className="workflow-warning"> · Faltan documentos en nodos iniciales</span>}
                    {anyNodeMissingInstructions && <span className="workflow-warning"> · Faltan instrucciones</span>}
                  </span>
                  <button
                    className="btn secondary"
                    onClick={() => { setWorkflowNodes([]); setWorkflowConnections([]); setSelectedNode(null); }}
                  >
                    <Trash2 size={16} />
                    Limpiar
                  </button>
                  <button
                    className="btn primary"
                    onClick={executeWorkflow}
                    disabled={isExecuting || anyNodeMissingInstructions || initialNodesMissingDocs}
                  >
                    {isExecuting ? (
                      <>
                        <Loader size={16} className="spin" />
                        Ejecutando...
                      </>
                    ) : (
                      <>
                        <Play size={16} />
                        Ejecutar Workflow
                      </>
                    )}
                  </button>
                </div>
                );
              })()}
            </div>
          )}

          {activeTab === 'agents' && (
            <div className="agents-tab">
              {/* Agentes Base */}
              <div className="agents-section">
                <h3 className="section-title">Agentes Base</h3>
                <p className="section-description">
                  Estos son los agentes fundamentales del sistema. Puedes crear agentes personalizados basados en ellos.
                </p>
                <div className="base-agents-grid">
                  {BASE_AGENTS.map(agent => (
                    <div key={agent.id} className="base-agent-card" style={{ borderColor: agent.color }}>
                      <div className="base-agent-header">
                        <span className="base-agent-icon" style={{ backgroundColor: agent.color + '20' }}>{agent.icon}</span>
                        <div className="base-agent-info">
                          <h4>{agent.name}</h4>
                          <span className="base-agent-type">{agent.description}</span>
                        </div>
                      </div>
                      <p className="base-agent-description">{agent.longDescription}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Mis Agentes */}
              <div className="agents-section">
                <div className="section-header">
                  <div>
                    <h3 className="section-title">Mis Agentes</h3>
                    <p className="section-description">
                      Agentes personalizados que has creado. Aparecen en la barra lateral de Tareas.
                    </p>
                  </div>
                  <button className="btn primary" onClick={() => openAgentModal()}>
                    <Plus size={16} />
                    Nuevo agente
                  </button>
                </div>

                {customAgents.length === 0 ? (
                  <div className="empty-state">
                    <Bot size={32} />
                    <p>No tienes agentes personalizados</p>
                    <span>Crea uno basado en Escritor o Analista para empezar</span>
                  </div>
                ) : (
                  <div className="custom-agents-list">
                    {customAgents.map(agent => {
                      const baseAgent = BASE_AGENTS.find(b => b.id === agent.base_agent_type) || BASE_AGENTS[0];
                      return (
                        <div
                          key={agent.id}
                          className="custom-agent-item clickable"
                          onClick={() => openAgentModal(agent)}
                        >
                          <div className="custom-agent-icon" style={{ backgroundColor: baseAgent.color + '20' }}>
                            {baseAgent.icon}
                          </div>
                          <div className="custom-agent-info">
                            <div className="custom-agent-name">{agent.name}</div>
                            <div className="custom-agent-meta">
                              Basado en: {baseAgent.name} · {AGENT_STYLES.find(s => s.value === agent.style)?.label || 'Formal'} · {AGENT_EXTENSIONS.find(e => e.value === agent.extension)?.label || 'Equilibrado'}
                            </div>
                          </div>
                          <ChevronRight size={18} className="custom-agent-arrow" />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'documents' && (
            <div className="documents-tab">
              {/* Contador total */}
              <div className="documents-summary">
                <div className="summary-info">
                  <FileText size={18} />
                  <span className="summary-count">{documents.length} documento{documents.length !== 1 ? 's' : ''}</span>
                  <span className="summary-hint">
                    Cada documento añade tiempo al análisis
                  </span>
                </div>
                <label className="btn-upload">
                  <Upload size={14} />
                  <span>{uploadLoading ? 'Subiendo...' : 'Subir documentos'}</span>
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.docx,.doc,.xlsx,.xls,.pptx,.ppt,.txt,.jpg,.jpeg,.png"
                    onChange={handleFileUpload}
                    disabled={uploadLoading || !currentProject}
                  />
                </label>
              </div>

              <div className="documents-panels">
                {/* Panel izquierdo - Lista de documentos */}
                <div className="panel documents-panel">
                  <div className="panel-header">
                    <div className="panel-title">
                      <h3>Biblioteca de Documentos</h3>
                    </div>
                    <button className="btn-icon" onClick={createFolder} title="Nueva carpeta">
                      <FolderPlus size={16} />
                    </button>
                  </div>

                  {/* Breadcrumb de navegación */}
                  {(currentFolder || folderPath.length > 0) && (
                    <div className="folder-breadcrumb">
                      <button
                        className="breadcrumb-item"
                        onClick={() => navigateToPathIndex(-1)}
                        onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('drag-over'); }}
                        onDragLeave={(e) => e.currentTarget.classList.remove('drag-over')}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.currentTarget.classList.remove('drag-over');
                          const docId = e.dataTransfer.getData('docId');
                          if (docId) moveDocumentToFolder(docId, null);
                        }}
                      >
                        <FolderOpen size={14} />
                        <span>Raíz</span>
                      </button>
                      {folderPath.map((folder, idx) => (
                        <React.Fragment key={folder.id}>
                          <ChevronRight size={14} className="breadcrumb-separator" />
                          <button
                            className={`breadcrumb-item ${idx === folderPath.length - 1 ? 'active' : ''}`}
                            onClick={() => navigateToPathIndex(idx)}
                            onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('drag-over'); }}
                            onDragLeave={(e) => e.currentTarget.classList.remove('drag-over')}
                            onDrop={(e) => {
                              e.preventDefault();
                              e.currentTarget.classList.remove('drag-over');
                              const docId = e.dataTransfer.getData('docId');
                              if (docId && idx < folderPath.length - 1) moveDocumentToFolder(docId, folder.id);
                            }}
                          >
                            <Folder size={14} />
                            <span>{folder.name}</span>
                          </button>
                        </React.Fragment>
                      ))}
                    </div>
                  )}

                  {/* Cabecera de ordenación */}
                  {documents.length > 0 && (
                    <div className="doc-list-header">
                      <div className="header-cell check-cell"></div>
                      <button className="header-cell name-cell sortable" onClick={() => toggleSort('name')}>
                        Nombre {sortBy === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
                      </button>
                      <button className="header-cell date-cell sortable" onClick={() => toggleSort('date')}>
                        Fecha {sortBy === 'date' && (sortOrder === 'asc' ? '↑' : '↓')}
                      </button>
                      <button className="header-cell status-cell sortable" onClick={() => toggleSort('status')}>
                        Estado {sortBy === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
                      </button>
                      <div className="header-cell preview-cell"></div>
                      <div className="header-cell actions-cell"></div>
                    </div>
                  )}

                  <div className="panel-body">
                    {documents.length === 0 ? (
                      <div className="empty-state">
                        <Upload size={32} />
                        <p>No hay documentos en este proyecto</p>
                        <span>Sube archivos PDF, Word, Excel, PowerPoint o imagenes para empezar</span>
                      </div>
                    ) : (
                      <div className="doc-list">
                        {getSortedDocuments().map(doc => {
                          const status = getDocStatus(doc);
                          const isReady = doc.status === 'completed' || doc.is_folder;
                          const isSelected = selectedDocs.includes(doc.id);
                          return (
                            <div
                              key={doc.id}
                              className={`doc-item ${status.class} ${isSelected ? 'selected' : ''} ${doc.is_folder ? 'is-folder' : ''}`}
                              draggable={isReady && !doc.is_folder}
                              onDragStart={(e) => {
                                if (doc.is_folder) return;
                                e.dataTransfer.setData('docId', doc.id);
                                e.dataTransfer.setData('docName', doc.filename);
                              }}
                              onDragOver={(e) => {
                                if (doc.is_folder) {
                                  e.preventDefault();
                                  e.currentTarget.classList.add('drag-over');
                                }
                              }}
                              onDragLeave={(e) => e.currentTarget.classList.remove('drag-over')}
                              onDrop={(e) => {
                                e.preventDefault();
                                e.currentTarget.classList.remove('drag-over');
                                if (doc.is_folder) {
                                  const docId = e.dataTransfer.getData('docId');
                                  if (docId && docId !== doc.id) {
                                    moveDocumentToFolder(docId, doc.id);
                                  }
                                }
                              }}
                              onDoubleClick={() => doc.is_folder && navigateToFolder(doc.id)}
                            >
                              <div className="doc-cell check-cell">
                                {isReady ? (
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleDocSelection(doc.id)}
                                  />
                                ) : (
                                  <span className={`status-dot ${status.class}`}>{status.icon}</span>
                                )}
                              </div>
                              <div
                                className="doc-cell name-cell"
                                onClick={() => doc.is_folder ? navigateToFolder(doc.id) : (isReady && setPreviewDoc(doc))}
                                title={doc.is_folder ? 'Doble clic para abrir' : 'Click para ver preview'}
                              >
                                {doc.is_folder ? (
                                  <Folder size={18} className="folder-icon" />
                                ) : (
                                  <span className="file-icon-emoji">{getFileIcon(doc.filename)}</span>
                                )}
                                <span className="doc-name">{doc.filename}</span>
                              </div>
                              <div className="doc-cell date-cell">
                                <span className="doc-date">{formatDate(doc.processed_at || doc.created_at)}</span>
                              </div>
                              <div className="doc-cell status-cell">
                                {doc.is_folder ? (
                                  <span className="status-badge folder">Carpeta</span>
                                ) : isReady ? (
                                  <span className="status-badge ready">Listo</span>
                                ) : (
                                  <span className={`status-badge ${status.class}`}>
                                    {doc.status === 'processing' && 'Procesando...'}
                                    {doc.status === 'pending' && 'En cola'}
                                    {doc.status === 'error' && 'Error'}
                                  </span>
                                )}
                              </div>
                              <div className="doc-cell preview-cell">
                                {isReady && !doc.is_folder && (
                                  <button
                                    className="btn-icon preview"
                                    onClick={(e) => { e.stopPropagation(); setPreviewDoc(doc); }}
                                    title="Ver resumen"
                                  >
                                    <Eye size={14} />
                                  </button>
                                )}
                              </div>
                              <div className="doc-cell actions-cell">
                                <button
                                  className="btn-icon delete"
                                  onClick={(e) => { e.stopPropagation(); handleDeleteDocument(doc.id); }}
                                  title="Eliminar documento"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {selectedDocs.length > 0 && (
                    <div className="panel-footer">
                      <span className="selection-count">
                        <Check size={14} />
                        {selectedDocs.length} seleccionado{selectedDocs.length !== 1 ? 's' : ''}
                      </span>
                      <button className="btn-text" onClick={() => setSelectedDocs([])}>Limpiar seleccion</button>
                    </div>
                  )}
                </div>

                {/* Panel derecho - Plantilla e Instrucciones */}
                <div className="config-panels">
                  {/* Plantilla */}
                  <div className="panel template-panel">
                    <div className="panel-header">
                      <div className="panel-title">
                        <Copy size={16} />
                        <h4>Documento Modelo</h4>
                      </div>
                    </div>
                    <div
                      className={`panel-body drop-zone ${templateDoc ? 'has-template' : ''}`}
                      onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('drag-over'); }}
                      onDragLeave={(e) => { e.currentTarget.classList.remove('drag-over'); }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.currentTarget.classList.remove('drag-over');
                        const docId = e.dataTransfer.getData('docId');
                        if (docId) setTemplateDoc(docId);
                      }}
                    >
                      {templateDoc ? (
                        <div className="template-doc">
                          <div className="template-doc-info">
                            <span className="file-icon-emoji large">{getFileIcon(documents.find(d => d.id === templateDoc)?.filename || '')}</span>
                            <div className="template-doc-details">
                              <span className="template-doc-name">
                                {documents.find(d => d.id === templateDoc)?.filename || 'Documento'}
                              </span>
                              <span className="template-doc-hint">El agente copiara esta estructura</span>
                            </div>
                          </div>
                          <button className="btn-icon remove" onClick={() => setTemplateDoc(null)} title="Quitar">
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <div className="empty-state template-empty">
                          <Copy size={24} />
                          <p>Arrastra aqui un documento modelo</p>
                          <span>El agente copiara su estructura y formato en la respuesta</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Instrucciones */}
                  <div className="panel instructions-panel">
                    <div className="panel-header">
                      <div className="panel-title">
                        <FileCheck size={16} />
                        <h4>Documento de Instrucciones</h4>
                      </div>
                    </div>
                    <div
                      className={`panel-body drop-zone ${instructionsDoc ? 'has-template' : ''}`}
                      onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('drag-over'); }}
                      onDragLeave={(e) => { e.currentTarget.classList.remove('drag-over'); }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.currentTarget.classList.remove('drag-over');
                        const docId = e.dataTransfer.getData('docId');
                        if (docId) setInstructionsDoc(docId);
                      }}
                    >
                      {instructionsDoc ? (
                        <div className="template-doc">
                          <div className="template-doc-info">
                            <span className="file-icon-emoji large">{getFileIcon(documents.find(d => d.id === instructionsDoc)?.filename || '')}</span>
                            <div className="template-doc-details">
                              <span className="template-doc-name">
                                {documents.find(d => d.id === instructionsDoc)?.filename || 'Documento'}
                              </span>
                              <span className="template-doc-hint">El agente seguira estas instrucciones</span>
                            </div>
                          </div>
                          <button className="btn-icon remove" onClick={() => setInstructionsDoc(null)} title="Quitar">
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <div className="empty-state template-empty">
                          <FileCheck size={24} />
                          <p>Arrastra aqui instrucciones adicionales</p>
                          <span>Un documento con guias o requisitos especificos para la tarea</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Acciones de documentos seleccionados */}
              {selectedDocs.length > 0 && (
                <div className="documents-action-bar">
                  <button
                    className="btn secondary"
                    onClick={() => {
                      selectedDocs.forEach(docId => {
                        const doc = documents.find(d => d.id === docId);
                        if (doc?.file_path) {
                          window.open(`${process.env.REACT_APP_API_URL || ''}/api/v1/projects/${currentProject.id}/documents/${docId}/download`, '_blank');
                        }
                      });
                    }}
                  >
                    <Download size={16} />
                    Descargar ({selectedDocs.length})
                  </button>
                  <button
                    className="btn danger"
                    onClick={async () => {
                      if (window.confirm(`¿Eliminar ${selectedDocs.length} documento${selectedDocs.length !== 1 ? 's' : ''}?`)) {
                        for (const docId of selectedDocs) {
                          await handleDeleteDocument(docId);
                        }
                        setSelectedDocs([]);
                      }
                    }}
                  >
                    <Trash2 size={16} />
                    Eliminar ({selectedDocs.length})
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'editor' && (
            <div className="editor-tab">
              {(isExecuting || isRefining) && (
                <div className="execution-status">
                  <div className="status-header">
                    <span className="status-text">{executionStatus}</span>
                    {isExecuting && <span className="status-progress">{executionProgress}%</span>}
                    {isRefining && <Loader size={14} className="spin" />}
                  </div>
                  {isExecuting && (
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{ width: `${executionProgress}%` }}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Pestañas de ejecuciones de workflow */}
              {workflowExecutions.length > 0 && (
                <div className="output-tabs">
                  {workflowExecutions.map(exec => (
                    <button
                      key={exec.id}
                      className={`output-tab ${activeExecutionId === exec.id ? 'active' : ''}`}
                      onClick={() => setActiveExecutionId(exec.id)}
                    >
                      <span className="output-tab-icons">
                        {exec.icons.map((icon, i) => (
                          <span key={i} className="output-tab-icon" style={{ backgroundColor: exec.colors[i] + '20' }}>
                            {icon}
                          </span>
                        ))}
                        {exec.icons.length > 1 && (
                          <span className="output-tab-arrow">→</span>
                        )}
                      </span>
                      <span className="output-tab-label">{exec.label}</span>
                      {exec.versions.length > 1 && (
                        <span className="output-tab-versions">v{exec.versions.length}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {/* Info del workflow activo */}
              {activeExecution && activeExecution.nodes && activeExecution.nodes.length > 0 && (
                <div className="workflow-info-bar">
                  <div className="workflow-info-agents">
                    <span className="workflow-info-label">Agentes:</span>
                    {activeExecution.nodes.map((node, idx) => (
                      <span key={idx} className="workflow-info-agent" style={{ borderColor: node.agentColor }}>
                        <span className="agent-icon">{node.agentIcon}</span>
                        {node.agentName}
                      </span>
                    ))}
                  </div>
                  <div className="workflow-info-docs">
                    <span className="workflow-info-label">Documentos:</span>
                    {[...new Set(activeExecution.nodes.flatMap(n => n.documents || []))].map((docId, idx) => {
                      const doc = documents.find(d => d.id === docId);
                      return doc ? (
                        <span key={idx} className="workflow-info-doc">
                          <FileText size={12} />
                          {doc.filename.length > 25 ? doc.filename.slice(0, 22) + '...' : doc.filename}
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>
              )}

              <div className="editor-content" ref={outputRef}>
                {isEditing ? (
                  <div className="editor-edit-mode">
                    <textarea
                      ref={editTextareaRef}
                      value={editableContent}
                      onChange={(e) => {
                        setEditableContent(e.target.value);
                        if (selectionMenu.visible && !selectionMenu.showInput) {
                          setSelectionMenu(prev => ({ ...prev, visible: false }));
                        }
                      }}
                      onMouseUp={handleTextSelection}
                      onKeyUp={(e) => {
                        if (e.shiftKey || ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                          handleTextSelection();
                        }
                      }}
                      className="edit-textarea"
                      placeholder="Escribe o edita el contenido..."
                    />
                    <div className="edit-actions">
                      <button className="btn secondary" onClick={cancelEditing}>
                        <X size={14} />
                        Cancelar
                      </button>
                      <button className="btn primary" onClick={saveEditing}>
                        <Check size={14} />
                        Guardar
                      </button>
                    </div>
                  </div>
                ) : currentOutput ? (
                  <div className="output-text">
                    {formatCitations(currentOutput).map((part, idx) => {
                      if (part.type === 'citation') {
                        return (
                          <span key={idx} className="citation" title={part.document}>
                            <FileText size={10} />
                            {part.number}
                          </span>
                        );
                      }
                      return <ReactMarkdown key={idx}>{part.content}</ReactMarkdown>;
                    })}
                    {isExecuting && <span className="cursor">|</span>}
                  </div>
                ) : (
                  <div className="editor-empty">
                    <Edit3 size={48} />
                    <p>Ejecuta una tarea para ver el resultado aqui</p>
                  </div>
                )}
              </div>

              {/* Barra de versiones */}
              {versions.length > 0 && !isEditing && (
                <div className="versions-bar">
                  <div className="versions-nav">
                    <button
                      className="version-nav-btn"
                      onClick={() => goToVersion(currentVersionIndex - 1)}
                      disabled={currentVersionIndex <= 0 || isRefining}
                    >
                      <ChevronRight size={16} style={{ transform: 'rotate(180deg)' }} />
                    </button>
                    <span className="version-indicator">
                      Version {currentVersionIndex + 1} de {versions.length}
                    </span>
                    <button
                      className="version-nav-btn"
                      onClick={() => goToVersion(currentVersionIndex + 1)}
                      disabled={currentVersionIndex >= versions.length - 1 || isRefining}
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                  <div className="versions-list">
                    {versions.map((version, idx) => (
                      <button
                        key={version.id}
                        className={`version-chip ${idx === currentVersionIndex ? 'active' : ''}`}
                        onClick={() => goToVersion(idx)}
                        disabled={isRefining}
                      >
                        <span className="version-label">{version.label}</span>
                        <span className="version-time">{version.timestamp}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {currentOutput && !isExecuting && !isEditing && (
                <div className="editor-actions">
                  <div className="refine-actions">
                    <span>Refinar:</span>
                    <button onClick={() => handleRefine('extend')} disabled={isRefining}>
                      {isRefining ? <Loader size={14} className="spin" /> : <Maximize2 size={14} />}
                      Extender
                    </button>
                    <button onClick={() => handleRefine('shorten')} disabled={isRefining}>
                      {isRefining ? <Loader size={14} className="spin" /> : <Scissors size={14} />}
                      Acortar
                    </button>
                    <button onClick={() => handleRefine('regenerate')} disabled={isRefining}>
                      {isRefining ? <Loader size={14} className="spin" /> : <RefreshCw size={14} />}
                      Regenerar
                    </button>
                  </div>
                  <div className="edit-export-actions">
                    <button onClick={startEditing} disabled={isRefining} className="edit-btn">
                      <Edit3 size={14} />
                      Editar
                    </button>
                    <button onClick={() => copyToClipboard('md')} disabled={isRefining}>
                      <Copy size={14} />
                      Copiar MD
                    </button>
                    <button onClick={() => copyToClipboard('text')} disabled={isRefining}>
                      <Copy size={14} />
                      Copiar texto
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'preview' && (
            <div className="preview-tab">
              {versions.length > 0 ? (
                <>
                  {/* Selector de version en preview */}
                  <div className="preview-versions-bar">
                    <span className="preview-versions-label">Versiones:</span>
                    <div className="preview-versions-chips">
                      {versions.map((version, idx) => (
                        <button
                          key={version.id}
                          className={`preview-version-chip ${idx === currentVersionIndex ? 'active' : ''}`}
                          onClick={() => goToVersion(idx)}
                        >
                          {version.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="preview-content">
                    <div className="preview-header">
                      <span className="preview-version-info">
                        {versions[currentVersionIndex]?.label} - {versions[currentVersionIndex]?.timestamp}
                      </span>
                    </div>
                    <div className="preview-body">
                      <ReactMarkdown>{versions[currentVersionIndex]?.content || ''}</ReactMarkdown>
                    </div>
                  </div>
                </>
              ) : (
                <div className="preview-empty">
                  <FileCheck size={48} />
                  <p>Ejecuta una tarea para ver el resultado aqui</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'chat' && (
            <div className="chat-tab">
              {!currentProject ? (
                <div className="chat-empty">
                  <MessageSquare size={48} />
                  <p>Selecciona un proyecto para chatear con sus documentos</p>
                </div>
              ) : (
                <div className="chat-layout">
                  <div className={`chat-docs-panel ${expandedSections.chatDocs !== false ? 'expanded' : 'collapsed'}`}>
                    <button
                      className="chat-docs-toggle"
                      onClick={() => setExpandedSections(prev => ({ ...prev, chatDocs: prev.chatDocs === false ? true : false }))}
                    >
                      <FileText size={16} />
                      <span>Documentos</span>
                      <span className="chat-docs-count">{chatFilterDocs.length}/{documents.filter(d => d.status === 'completed' && !d.is_folder).length}</span>
                      {expandedSections.chatDocs !== false ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </button>

                    {expandedSections.chatDocs !== false && (
                      <div className="chat-docs-list">
                        {documents.filter(d => d.status === 'completed' && !d.is_folder).length === 0 ? (
                          <div className="chat-docs-empty">No hay documentos procesados</div>
                        ) : (
                          <>
                            <div className="chat-docs-actions">
                              <button
                                className="chat-docs-select-all"
                                onClick={() => setChatFilterDocs(documents.filter(d => d.status === 'completed' && !d.is_folder).map(d => d.id))}
                              >
                                Seleccionar todos
                              </button>
                              {chatFilterDocs.length > 0 && (
                                <button className="chat-docs-clear" onClick={() => setChatFilterDocs([])}>
                                  Limpiar
                                </button>
                              )}
                            </div>
                            {documents.filter(d => d.status === 'completed' && !d.is_folder).map(doc => (
                              <label key={doc.id} className={`chat-doc-item ${chatFilterDocs.includes(doc.id) ? 'selected' : ''}`}>
                                <input
                                  type="checkbox"
                                  checked={chatFilterDocs.includes(doc.id)}
                                  onChange={() => toggleChatDocFilter(doc.id)}
                                />
                                <span className="chat-doc-icon">{getFileIcon(doc.filename)}</span>
                                <span className="chat-doc-name" title={doc.filename}>
                                  {doc.filename.length > 28 ? doc.filename.slice(0, 28) + '...' : doc.filename}
                                </span>
                              </label>
                            ))}
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="chat-main">
                    <div className="chat-messages">
                      {chatMessages.length === 0 ? (
                        <div className="chat-welcome">
                          <div className="chat-welcome-icon">
                            <Bot size={32} />
                          </div>
                          <h3>Chat con documentos</h3>
                          <p>Selecciona documentos del panel izquierdo y haz preguntas sobre su contenido.</p>
                          {chatFilterDocs.length === 0 ? (
                            <div className="chat-welcome-hint">
                              <FileText size={16} />
                              <span>Selecciona al menos un documento para comenzar</span>
                            </div>
                          ) : (
                            <div className="chat-welcome-ready">
                              <Check size={16} />
                              <span>{chatFilterDocs.length} documento{chatFilterDocs.length > 1 ? 's' : ''} seleccionado{chatFilterDocs.length > 1 ? 's' : ''}</span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <>
                          {chatMessages.map((msg, idx) => (
                            <div key={idx} className={`chat-message ${msg.role} ${msg.error ? 'error' : ''}`}>
                              <div className="chat-message-avatar">
                                {msg.role === 'user' ? '👤' : '🤖'}
                              </div>
                              <div className="chat-message-bubble">
                                <ReactMarkdown>{msg.content}</ReactMarkdown>
                                {msg.sources && msg.sources.length > 0 && (
                                  <div className="chat-sources">
                                    <span className="chat-sources-label">Fuentes consultadas:</span>
                                    <div className="chat-sources-list">
                                      {[...new Set(msg.sources.map(s => s.document_name))].map((docName, i) => (
                                        <span key={i} className="chat-source-tag" title={docName}>
                                          {getFileIcon(docName)} {docName.slice(0, 30)}{docName.length > 30 ? '...' : ''}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                          <div ref={chatEndRef} />
                        </>
                      )}
                      {chatLoading && (
                        <div className="chat-message assistant">
                          <div className="chat-message-avatar">🤖</div>
                          <div className="chat-message-bubble loading">
                            <div className="chat-typing">
                              <span></span><span></span><span></span>
                            </div>
                            <span>Buscando en documentos...</span>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="chat-input-area">
                      {chatMessages.length > 0 && (
                        <button className="chat-new-btn" onClick={clearChat} title="Nueva conversación">
                          <Plus size={16} />
                        </button>
                      )}
                      <div className="chat-input-wrapper">
                        <input
                          type="text"
                          className="chat-input"
                          placeholder={chatFilterDocs.length === 0 ? "Selecciona documentos primero..." : "Escribe tu pregunta..."}
                          value={chatInput}
                          onChange={(e) => setChatInput(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendChatMessage()}
                          disabled={chatLoading || chatFilterDocs.length === 0}
                        />
                      </div>
                      <button
                        className="chat-send-btn"
                        onClick={sendChatMessage}
                        disabled={!chatInput.trim() || chatLoading || chatFilterDocs.length === 0}
                        title={chatFilterDocs.length === 0 ? "Selecciona documentos primero" : "Enviar mensaje"}
                      >
                        {chatLoading ? <Loader size={18} className="spin" /> : <Play size={18} />}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* PANEL DERECHO */}
      <aside className={`sidebar-right ${panelCollapsed ? 'collapsed' : ''}`}>
        <button
          className="panel-toggle"
          onClick={() => setPanelCollapsed(!panelCollapsed)}
        >
          {panelCollapsed ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>

        {!panelCollapsed && (
          <>
            <div className="panel-header">
              <BarChart2 size={16} />
              <span>Estado</span>
            </div>

            {/* Agentes activos */}
            {activeAgents.length > 0 && (
              <div className="panel-section">
                <h4>Agentes Trabajando</h4>
                {activeAgents.map(agent => (
                  <div key={agent.id} className="agent-card active">
                    <div className="agent-card-header">
                      <span className="agent-icon">{agent.icon}</span>
                      <span className="agent-name">{agent.name}</span>
                    </div>
                    <div className="agent-card-task">{agent.task}</div>
                    <div className="agent-card-status">{agent.status}</div>
                    <div className="agent-progress">
                      <div
                        className="agent-progress-fill"
                        style={{ width: `${agent.progress}%` }}
                      />
                    </div>
                    <div className="agent-card-meta">
                      <span><Clock size={12} /> {Math.floor((Date.now() - agent.startTime) / 1000)}s</span>
                      <span><Zap size={12} /> {agent.tokens} tokens</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Cola */}
            {taskQueue.length > 0 && (
              <div className="panel-section">
                <h4>Cola ({taskQueue.length})</h4>
                <div className="queue-mini">
                  {taskQueue.slice(0, 3).map((task, idx) => (
                    <div key={task.id} className="queue-mini-item">
                      <span>{idx + 1}. {task.agentIcon}</span>
                      <span>{task.label}</span>
                    </div>
                  ))}
                  {taskQueue.length > 3 && (
                    <div className="queue-mini-more">+{taskQueue.length - 3} más</div>
                  )}
                </div>
              </div>
            )}

            {/* Completadas */}
            {completedTasks.length > 0 && (
              <div className="panel-section">
                <h4>Completadas ({completedTasks.length})</h4>
                <div className="completed-list">
                  {completedTasks.slice(-5).map((task, idx) => (
                    <div key={idx} className="completed-item">
                      <Check size={12} />
                      <span>{task.label}</span>
                      <span className="completed-tokens">{task.tokens}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Estadisticas */}
            <div className="panel-section stats">
              <h4>Estadisticas</h4>
              <div className="stat-row">
                <span>Tokens usados</span>
                <span>{stats.tokens.toLocaleString()}</span>
              </div>
              <div className="stat-row">
                <span>Tareas completadas</span>
                <span>{stats.tasks}</span>
              </div>
              <div className="stat-row">
                <span>Tiempo ejecución</span>
                <span>{Math.floor(stats.time / 60)}:{(stats.time % 60).toString().padStart(2, '0')}</span>
              </div>
            </div>
          </>
        )}

        {/* Tiempo de sesión - siempre visible abajo */}
        {currentSession && (
          <div className="session-time-footer">
            <div className="session-time-row">
              <div className="session-time-item">
                <span className="session-time-label">Sesión actual</span>
                <span className="session-time-value current">
                  {Math.floor(sessionTime / 3600)}:{Math.floor((sessionTime % 3600) / 60).toString().padStart(2, '0')}:{(sessionTime % 60).toString().padStart(2, '0')}
                </span>
              </div>
              <div className="session-time-item">
                <span className="session-time-label">Tiempo total</span>
                <span className="session-time-value total">
                  {Math.floor((totalSessionTime + sessionTime) / 3600)}:{Math.floor(((totalSessionTime + sessionTime) % 3600) / 60).toString().padStart(2, '0')}:{((totalSessionTime + sessionTime) % 60).toString().padStart(2, '0')}
                </span>
              </div>
            </div>
            {timeEntries.length > 0 && (
              <div className="session-time-history">
                <button
                  className="session-time-history-toggle"
                  onClick={() => setShowTimeHistory(!showTimeHistory)}
                >
                  <Clock size={12} />
                  <span>Historial ({timeEntries.length})</span>
                  {showTimeHistory ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                </button>
                {showTimeHistory && (
                  <div className="session-time-entries">
                    {timeEntries.map(entry => (
                      <div key={entry.id} className="session-time-entry">
                        <span className="entry-date">
                          {new Date(entry.started_at).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' })}
                        </span>
                        <span className="entry-time">
                          {new Date(entry.started_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        <span className="entry-duration">
                          {Math.floor(entry.duration_seconds / 60)}:{(entry.duration_seconds % 60).toString().padStart(2, '0')}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </aside>

      {/* Agent Modal */}
      {showAgentModal && (
        <div className="modal-overlay" onClick={() => setShowAgentModal(false)}>
          <div className="agent-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingAgent ? 'Editar Agente' : 'Crear Agente'}</h3>
              <button className="btn-icon" onClick={() => setShowAgentModal(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Nombre del agente</label>
                <input
                  type="text"
                  value={agentForm.name}
                  onChange={(e) => setAgentForm({ ...agentForm, name: e.target.value })}
                  placeholder="Ej: Redactor de Memorias"
                />
              </div>

              {!editingAgent && (
                <div className="form-group">
                  <label>Basado en</label>
                  <div className="base-agent-selector">
                    {BASE_AGENTS.map(agent => (
                      <button
                        key={agent.id}
                        className={`base-agent-option ${agentForm.base_agent_type === agent.id ? 'selected' : ''}`}
                        onClick={() => setAgentForm({ ...agentForm, base_agent_type: agent.id })}
                        style={{ borderColor: agentForm.base_agent_type === agent.id ? agent.color : 'transparent' }}
                      >
                        <span className="option-icon">{agent.icon}</span>
                        <span className="option-name">{agent.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Estilo</label>
                  <select
                    value={agentForm.style}
                    onChange={(e) => setAgentForm({ ...agentForm, style: e.target.value })}
                  >
                    {AGENT_STYLES.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Extension</label>
                  <select
                    value={agentForm.extension}
                    onChange={(e) => setAgentForm({ ...agentForm, extension: e.target.value })}
                  >
                    {AGENT_EXTENSIONS.map(e => (
                      <option key={e.value} value={e.value}>{e.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Instrucciones especificas</label>
                <textarea
                  value={agentForm.system_prompt}
                  onChange={(e) => setAgentForm({ ...agentForm, system_prompt: e.target.value })}
                  placeholder="Describe como debe comportarse este agente, que debe priorizar, que formato usar..."
                  rows={5}
                />
              </div>
            </div>
            <div className="modal-footer">
              {editingAgent && (
                <button
                  className="btn danger"
                  onClick={() => {
                    deleteAgent(editingAgent.id);
                    setShowAgentModal(false);
                  }}
                >
                  <Trash2 size={14} />
                  Eliminar
                </button>
              )}
              <div className="modal-footer-right">
                <button className="btn secondary" onClick={() => setShowAgentModal(false)}>
                  Cancelar
                </button>
                <button
                  className="btn primary"
                  onClick={saveAgent}
                  disabled={!agentForm.name.trim()}
                >
                  {editingAgent ? 'Guardar cambios' : 'Crear agente'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewDoc && (
        <div className="preview-modal-overlay" onClick={() => setPreviewDoc(null)}>
          <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="preview-modal-header">
              <div className="preview-modal-title">
                <span className="file-icon-emoji large">{getFileIcon(previewDoc.filename)}</span>
                <div>
                  <h3>{previewDoc.filename}</h3>
                  <div className="preview-modal-meta">
                    <span>Subido: {formatDate(previewDoc.created_at)}</span>
                    {previewDoc.processed_at && (
                      <span>Procesado: {formatDate(previewDoc.processed_at)}</span>
                    )}
                  </div>
                </div>
              </div>
              <button className="preview-modal-close" onClick={() => setPreviewDoc(null)}>
                <X size={18} />
              </button>
            </div>
            <div className="preview-modal-body">
              {previewDoc.summary ? (
                <div className="preview-modal-content">
                  <ReactMarkdown>{previewDoc.summary}</ReactMarkdown>
                </div>
              ) : (
                <div className="preview-modal-empty">
                  <FileText size={48} />
                  <p>Este documento aun no tiene resumen disponible</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Selection Menu */}
      {selectionMenu.visible && (
        <div
          className={`selection-menu ${selectionMenu.showResult ? 'selection-menu-large' : ''}`}
          style={{
            position: 'fixed',
            left: selectionMenu.x,
            top: selectionMenu.y,
            transform: selectionMenu.showResult ? 'translate(-50%, 20px)' : 'translate(-50%, -100%)'
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {selectionMenu.showResult ? (
            <div className="selection-menu-result">
              <div className="selection-menu-result-header">
                <span>{selectionMenu.inputMode === 'edit' ? 'Resultado de la edicion:' : 'Respuesta:'}</span>
                <button onClick={closeSelectionMenu} title="Cerrar">
                  <X size={14} />
                </button>
              </div>
              <div className="selection-menu-result-content">
                <ReactMarkdown>{selectionMenu.result}</ReactMarkdown>
              </div>
              <div className="selection-menu-result-actions">
                {selectionMenu.inputMode === 'edit' ? (
                  <button className="primary" onClick={() => applySelectionResult('replace')}>
                    <Check size={14} />
                    Reemplazar texto
                  </button>
                ) : (
                  <button className="primary" onClick={() => applySelectionResult('insert')}>
                    <Plus size={14} />
                    Insertar en texto
                  </button>
                )}
                <button onClick={() => applySelectionResult('copy')}>
                  <Copy size={14} />
                  Copiar
                </button>
                <button onClick={closeSelectionMenu}>
                  Descartar
                </button>
              </div>
            </div>
          ) : selectionMenu.showInput ? (
            <div className="selection-menu-input">
              <div className="selection-menu-input-header">
                <span>
                  {selectionMenu.inputMode === 'edit' ? 'Instrucciones de edicion:' : 'Tu pregunta:'}
                </span>
                <button onClick={() => setSelectionMenu(prev => ({ ...prev, showInput: false, inputMode: null, inputValue: '' }))}>
                  <X size={14} />
                </button>
              </div>
              <textarea
                autoFocus
                placeholder={selectionMenu.inputMode === 'edit' ? 'Ej: Hazlo mas formal...' : 'Ej: Que significa esto?'}
                value={selectionMenu.inputValue}
                onChange={(e) => setSelectionMenu(prev => ({ ...prev, inputValue: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    executeSelectionAction();
                  }
                }}
              />
              <div className="selection-menu-input-actions">
                <button onClick={closeSelectionMenu}>
                  Cancelar
                </button>
                <button
                  className="primary"
                  onClick={executeSelectionAction}
                  disabled={selectionLoading || (selectionMenu.inputMode === 'edit' && !selectionMenu.inputValue.trim())}
                >
                  {selectionLoading ? <Loader size={14} className="spin" /> : <Check size={14} />}
                  {selectionMenu.inputMode === 'edit' ? 'Aplicar' : 'Enviar'}
                </button>
              </div>
            </div>
          ) : (
            <div className="selection-menu-buttons">
              <button onClick={() => handleSelectionAction('edit')} title="Editar con instrucciones">
                <Edit3 size={14} />
                Editar
              </button>
              <button onClick={() => handleSelectionAction('explain')} title="Preguntar sobre este texto">
                <MessageSquare size={14} />
                Preguntar
              </button>
              <button onClick={() => handleSelectionAction('copy')} title="Copiar texto">
                <Copy size={14} />
                Copiar
              </button>
              <button onClick={closeSelectionMenu} className="close-btn" title="Cerrar">
                <X size={14} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Toast Notifications */}
      <div className="toast-container">
        {toasts.map(toast => (
          <div key={toast.id} className={`toast toast-${toast.type}`}>
            {toast.type === 'success' && <Check size={16} />}
            {toast.type === 'error' && <X size={16} />}
            {toast.type === 'info' && <FileText size={16} />}
            <span>{toast.message}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default WorkspacePage;
